import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Save, ArrowLeft, BookOpen, FileText, Tag,
  Loader2, X, Copy, Download, Edit3, Search, RefreshCw,
  ChevronDown, ChevronRight, CopyPlus, BookMarked
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  { value: 'doctrine', label: 'Doctrine', color: 'bg-blue-100 text-blue-700 border-blue-300', accent: '#1B3A6B' },
  { value: 'illustration', label: 'Illustration', color: 'bg-amber-100 text-amber-700 border-amber-300', accent: '#B8860B' },
  { value: 'application', label: 'Application', color: 'bg-green-100 text-green-700 border-green-300', accent: '#059669' },
];

export default function SermonNotes({ onClose }) {
  const [view, setView] = useState('list'); // list | edit
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { loadNotes(); }, [filterCat]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sermon-notes/list${filterCat ? `?category=${filterCat}` : ''}`);
      setNotes(res.data.notes);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createNew = async () => {
    try {
      const res = await axios.post(`${API}/sermon-notes/create`, {
        title: 'New Sermon', category: 'doctrine',
        subtopics: [{ id: crypto.randomUUID(), title: '', points: [], content: '', category: 'doctrine' }]
      });
      setNote(res.data);
      setView('edit');
    } catch (err) { alert('Failed to create'); }
  };

  const loadNote = async (id) => {
    try {
      const res = await axios.get(`${API}/sermon-notes/${id}`);
      setNote(res.data);
      setView('edit');
    } catch (err) { alert('Failed to load'); }
  };

  const saveNote = async () => {
    if (!note) return;
    setSaving(true);
    try {
      await axios.put(`${API}/sermon-notes/${note.id}`, {
        title: note.title, main_text: note.main_text, main_scripture: note.main_scripture,
        supporting_scriptures: note.supporting_scriptures, body: note.body,
        subtopics: note.subtopics, category: note.category, tags: note.tags,
      });
    } catch (err) { alert('Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Delete this sermon note?')) return;
    try { await axios.delete(`${API}/sermon-notes/${id}`); loadNotes(); if (note?.id === id) { setNote(null); setView('list'); } }
    catch (err) { alert('Failed'); }
  };

  const duplicateNote = async (id) => {
    try { await axios.post(`${API}/sermon-notes/${id}/duplicate`); loadNotes(); }
    catch (err) { alert('Failed'); }
  };

  const exportPPTX = (id) => window.open(`${API}/sermon-notes/${id}/export-pptx`, '_blank');

  const updateSubtopic = (idx, updates) => {
    const subs = [...(note.subtopics || [])];
    subs[idx] = { ...subs[idx], ...updates };
    setNote({ ...note, subtopics: subs });
  };

  const addSubtopic = () => {
    const subs = [...(note.subtopics || []), { id: crypto.randomUUID(), title: '', points: [''], content: '', category: 'doctrine' }];
    setNote({ ...note, subtopics: subs });
  };

  const removeSubtopic = (idx) => {
    const subs = (note.subtopics || []).filter((_, i) => i !== idx);
    setNote({ ...note, subtopics: subs });
  };

  const addPoint = (stIdx) => {
    const subs = [...(note.subtopics || [])];
    subs[stIdx] = { ...subs[stIdx], points: [...(subs[stIdx].points || []), ''] };
    setNote({ ...note, subtopics: subs });
  };

  const updatePoint = (stIdx, ptIdx, value) => {
    const subs = [...(note.subtopics || [])];
    const pts = [...(subs[stIdx].points || [])];
    pts[ptIdx] = value;
    subs[stIdx] = { ...subs[stIdx], points: pts };
    setNote({ ...note, subtopics: subs });
  };

  const removePoint = (stIdx, ptIdx) => {
    const subs = [...(note.subtopics || [])];
    subs[stIdx] = { ...subs[stIdx], points: subs[stIdx].points.filter((_, i) => i !== ptIdx) };
    setNote({ ...note, subtopics: subs });
  };

  const addScripture = () => {
    setNote({ ...note, supporting_scriptures: [...(note.supporting_scriptures || []), ''] });
  };

  const updateScripture = (idx, val) => {
    const s = [...(note.supporting_scriptures || [])];
    s[idx] = val;
    setNote({ ...note, supporting_scriptures: s });
  };

  const removeScripture = (idx) => {
    setNote({ ...note, supporting_scriptures: (note.supporting_scriptures || []).filter((_, i) => i !== idx) });
  };

  const filtered = notes.filter(n => !searchTerm || n.title?.toLowerCase().includes(searchTerm.toLowerCase()) || n.main_scripture?.toLowerCase().includes(searchTerm.toLowerCase()));

  const getCatStyle = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[0];

  // ═══════════════ LIST VIEW ═══════════════
  if (view === 'list') {
    return (
      <div data-testid="sermon-notes">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#1B3A6B]">Sermon Notes</h2>
            <p className="text-xs sm:text-sm text-[#475569] mt-1">Create and organize your preaching notes</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={createNew} className="btn-primary py-2 px-4 rounded-lg font-medium flex items-center gap-2 text-sm" data-testid="create-sermon-note">
              <Plus className="w-4 h-4" /> New Sermon
            </button>
            <button onClick={loadNotes} className="p-2 rounded-lg border border-[#E2DFD8] hover:bg-[#F4F2EC]"><RefreshCw className="w-4 h-4 text-[#475569]" /></button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search sermons..." className="w-full pl-9 pr-3 py-2 border border-[#E2DFD8] rounded-lg text-sm" data-testid="sermon-search" />
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => setFilterCat('')} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${!filterCat ? 'bg-[#1B3A6B] text-white border-[#1B3A6B]' : 'border-[#E2DFD8] text-[#475569] hover:bg-[#F4F2EC]'}`}>All</button>
            {CATEGORIES.map(c => (
              <button key={c.value} onClick={() => setFilterCat(c.value)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${filterCat === c.value ? c.color + ' border' : 'border-[#E2DFD8] text-[#475569] hover:bg-[#F4F2EC]'}`}>{c.label}</button>
            ))}
          </div>
        </div>

        {/* Notes List */}
        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl shadow-sm">
            <BookOpen className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
            <p className="text-[#475569] mb-4">{notes.length === 0 ? 'No sermon notes yet' : 'No matches'}</p>
            {notes.length === 0 && <button onClick={createNew} className="btn-primary py-2 px-6 rounded-lg text-sm">Create Your First Sermon</button>}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(n => {
              const cat = getCatStyle(n.category);
              return (
                <div key={n.id} className="bg-white rounded-xl shadow-sm border border-[#E2DFD8] p-4 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => loadNote(n.id)} data-testid={`sermon-card-${n.id}`}>
                  <div className="w-1 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: cat.accent }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="font-semibold text-[#0F1A2A] text-sm sm:text-base truncate">{n.title}</h3>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${cat.color} flex-shrink-0`}>{cat.label}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#94A3B8]">
                      {n.main_scripture && <span className="flex items-center gap-1"><BookMarked className="w-3 h-3" />{n.main_scripture}</span>}
                      <span>{n.subtopic_count} subtopic(s)</span>
                      <span>{new Date(n.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={e => { e.stopPropagation(); exportPPTX(n.id); }} className="p-1.5 hover:bg-[#F4F2EC] rounded" title="Export PPTX"><Download className="w-3.5 h-3.5 text-[#475569]" /></button>
                    <button onClick={e => { e.stopPropagation(); duplicateNote(n.id); }} className="p-1.5 hover:bg-blue-50 rounded" title="Duplicate"><CopyPlus className="w-3.5 h-3.5 text-blue-600" /></button>
                    <button onClick={e => { e.stopPropagation(); deleteNote(n.id); }} className="p-1.5 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ═══════════════ EDIT VIEW ═══════════════
  if (!note) return null;

  return (
    <div data-testid="sermon-note-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => { setView('list'); loadNotes(); }} className="p-2 hover:bg-[#F4F2EC] rounded-lg flex-shrink-0" data-testid="sermon-back">
            <ArrowLeft className="w-5 h-5 text-[#475569]" />
          </button>
          <input type="text" value={note.title} onChange={e => setNote({ ...note, title: e.target.value })} className="text-lg sm:text-xl font-bold text-[#0F1A2A] bg-transparent border-none focus:ring-0 p-0 flex-1 min-w-0" placeholder="Sermon title" data-testid="sermon-title" />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select value={note.category} onChange={e => setNote({ ...note, category: e.target.value })} className="text-xs px-2 py-1.5 border border-[#E2DFD8] rounded-lg" data-testid="sermon-category">
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <button onClick={() => exportPPTX(note.id)} className="px-3 py-1.5 border border-[#E2DFD8] rounded-lg text-xs flex items-center gap-1.5 hover:bg-[#F4F2EC]" data-testid="sermon-export">
            <Download className="w-3.5 h-3.5" /> PPTX
          </button>
          <button onClick={saveNote} disabled={saving} className="btn-primary py-1.5 px-4 rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50" data-testid="sermon-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {/* Main Scripture */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E2DFD8] p-4">
          <label className="text-xs font-bold text-[#0F1A2A] uppercase tracking-wider mb-2 block">Main Scripture</label>
          <input type="text" value={note.main_scripture || ''} onChange={e => setNote({ ...note, main_scripture: e.target.value })} placeholder="e.g., John 3:16-17" className="w-full px-3 py-2 border border-[#E2DFD8] rounded-lg text-sm focus:ring-2 focus:ring-[#1B3A6B]" data-testid="sermon-main-scripture" />
        </div>

        {/* Main Text / Body */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E2DFD8] p-4">
          <label className="text-xs font-bold text-[#0F1A2A] uppercase tracking-wider mb-2 block">Main Text / Sermon Body</label>
          <div className="border border-[#E2DFD8] rounded-lg overflow-hidden">
            <div className="flex items-center gap-1 px-2 py-1.5 bg-[#F9F8F6] border-b border-[#E2DFD8]">
              <button onClick={() => document.execCommand('bold')} className="p-1 hover:bg-[#E2DFD8] rounded text-xs font-bold">B</button>
              <button onClick={() => document.execCommand('italic')} className="p-1 hover:bg-[#E2DFD8] rounded text-xs italic">I</button>
              <button onClick={() => document.execCommand('underline')} className="p-1 hover:bg-[#E2DFD8] rounded text-xs underline">U</button>
              <span className="w-px h-4 bg-[#E2DFD8] mx-1" />
              <button onClick={() => document.execCommand('insertUnorderedList')} className="p-1 hover:bg-[#E2DFD8] rounded text-xs">• List</button>
              <button onClick={() => document.execCommand('insertOrderedList')} className="p-1 hover:bg-[#E2DFD8] rounded text-xs">1. List</button>
            </div>
            <div
              contentEditable
              className="min-h-[120px] px-3 py-2 text-sm focus:outline-none prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: note.main_text || '' }}
              onBlur={e => setNote(prev => ({ ...prev, main_text: e.target.innerHTML }))}
              data-testid="sermon-main-text"
            />
          </div>
        </div>

        {/* Supporting Scriptures */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E2DFD8] p-4">
          <label className="text-xs font-bold text-[#0F1A2A] uppercase tracking-wider mb-2 block">Supporting Scriptures</label>
          <div className="space-y-2">
            {(note.supporting_scriptures || []).map((sc, i) => (
              <div key={i} className="flex items-center gap-2">
                <BookMarked className="w-4 h-4 text-[#B8860B] flex-shrink-0" />
                <input type="text" value={sc} onChange={e => updateScripture(i, e.target.value)} placeholder="e.g., Romans 8:28" className="flex-1 px-3 py-1.5 border border-[#E2DFD8] rounded-lg text-sm" />
                <button onClick={() => removeScripture(i)} className="p-1 hover:bg-red-50 rounded"><X className="w-3.5 h-3.5 text-red-400" /></button>
              </div>
            ))}
            <button onClick={addScripture} className="text-xs text-[#1B3A6B] hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add scripture reference</button>
          </div>
        </div>

        {/* Subtopics */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-[#0F1A2A] uppercase tracking-wider">Subtopics & Points</h3>
            <button onClick={addSubtopic} className="text-xs text-[#1B3A6B] hover:underline flex items-center gap-1" data-testid="add-subtopic"><Plus className="w-3 h-3" /> Add Subtopic</button>
          </div>
          <div className="space-y-3">
            {(note.subtopics || []).map((st, stIdx) => {
              const stCat = getCatStyle(st.category);
              return (
                <div key={st.id || stIdx} className="bg-white rounded-xl shadow-sm border border-[#E2DFD8] overflow-hidden" data-testid={`subtopic-${stIdx}`}>
                  {/* Subtopic header */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-[#F4F2EC]">
                    <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: stCat.accent }} />
                    <input type="text" value={st.title} onChange={e => updateSubtopic(stIdx, { title: e.target.value })} placeholder="Subtopic title" className="flex-1 text-sm font-semibold bg-transparent border-none focus:ring-0 p-0" />
                    <select value={st.category || 'doctrine'} onChange={e => updateSubtopic(stIdx, { category: e.target.value })} className="text-[10px] px-1.5 py-1 border border-[#E2DFD8] rounded" data-testid={`subtopic-cat-${stIdx}`}>
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <button onClick={() => removeSubtopic(stIdx)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                  </div>
                  {/* Subtopic content */}
                  <div className="px-4 py-3 space-y-2">
                    {/* Rich text content area */}
                    <div className="border border-[#E2DFD8] rounded-lg overflow-hidden">
                      <div className="flex items-center gap-1 px-2 py-1 bg-[#F9F8F6] border-b border-[#E2DFD8]">
                        <button onClick={() => document.execCommand('bold')} className="p-0.5 hover:bg-[#E2DFD8] rounded text-[10px] font-bold">B</button>
                        <button onClick={() => document.execCommand('italic')} className="p-0.5 hover:bg-[#E2DFD8] rounded text-[10px] italic">I</button>
                        <button onClick={() => document.execCommand('underline')} className="p-0.5 hover:bg-[#E2DFD8] rounded text-[10px] underline">U</button>
                      </div>
                      <div
                        contentEditable
                        className="min-h-[60px] px-3 py-2 text-xs focus:outline-none"
                        dangerouslySetInnerHTML={{ __html: st.content || '' }}
                        onBlur={e => updateSubtopic(stIdx, { content: e.target.innerHTML })}
                      />
                    </div>
                    {/* Points */}
                    <div>
                      <label className="text-[10px] text-[#94A3B8] uppercase tracking-wider font-medium">Key Points</label>
                      {(st.points || []).map((pt, ptIdx) => (
                        <div key={ptIdx} className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-[#94A3B8] w-4 flex-shrink-0">{ptIdx + 1}.</span>
                          <input type="text" value={pt} onChange={e => updatePoint(stIdx, ptIdx, e.target.value)} placeholder="Point..." className="flex-1 px-2 py-1 border border-[#E2DFD8] rounded text-xs" />
                          <button onClick={() => removePoint(stIdx, ptIdx)} className="p-0.5 hover:bg-red-50 rounded"><X className="w-3 h-3 text-red-400" /></button>
                        </div>
                      ))}
                      <button onClick={() => addPoint(stIdx)} className="text-[10px] text-[#1B3A6B] hover:underline flex items-center gap-0.5 mt-1"><Plus className="w-2.5 h-2.5" /> Add point</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Notes/Body (freeform) */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E2DFD8] p-4">
          <label className="text-xs font-bold text-[#0F1A2A] uppercase tracking-wider mb-2 block">Additional Notes</label>
          <div className="border border-[#E2DFD8] rounded-lg overflow-hidden">
            <div className="flex items-center gap-1 px-2 py-1.5 bg-[#F9F8F6] border-b border-[#E2DFD8]">
              <button onClick={() => document.execCommand('bold')} className="p-1 hover:bg-[#E2DFD8] rounded text-xs font-bold">B</button>
              <button onClick={() => document.execCommand('italic')} className="p-1 hover:bg-[#E2DFD8] rounded text-xs italic">I</button>
              <button onClick={() => document.execCommand('underline')} className="p-1 hover:bg-[#E2DFD8] rounded text-xs underline">U</button>
              <button onClick={() => document.execCommand('insertUnorderedList')} className="p-1 hover:bg-[#E2DFD8] rounded text-xs">• List</button>
            </div>
            <div
              contentEditable
              className="min-h-[80px] px-3 py-2 text-sm focus:outline-none prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: note.body || '' }}
              onBlur={e => setNote(prev => ({ ...prev, body: e.target.innerHTML }))}
              data-testid="sermon-body"
            />
          </div>
        </div>

        {/* Tags */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E2DFD8] p-4">
          <label className="text-xs font-bold text-[#0F1A2A] uppercase tracking-wider mb-2 block">Tags</label>
          <input type="text" value={(note.tags || []).join(', ')} onChange={e => setNote({ ...note, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })} placeholder="e.g., faith, prayer, grace (comma separated)" className="w-full px-3 py-2 border border-[#E2DFD8] rounded-lg text-sm" />
        </div>
      </div>
    </div>
  );
}
